import { Params, useParams } from 'react-router-dom';
import Breadcrumb from '../../components/Breadcrumb';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_KEY, API_SECRET, BASEURL } from '../Constants';
import { getAccessToken } from '../../services/http-common';
import AuthenticationService from '../../services/AuthenticationService';

interface OrderDetails {
  orderDetailsId?: number | null;
  orderId: number;
  price: string;
  productId: number;
  productImageUrl: string;
  productName: string;
  quantity: string;
  option: string;
}
interface orderInterface {
  orderId?: any | null;
  amount: string;
  date: string;
  customerId: string;
  shippingAddress: string;
  flatShipping: string;
  pincode: string;
  state: string;
  paymentMode: string;
  paymentId: string;
  paymentOrderId: string;
  paymentStatus: string;
  status: string;
  orderTotal: string;
  orderDetails: OrderDetails[];
  // Coupon/Discount fields - updated to match backend response
  appliedCouponCode?: string;
  discountAmount?: string;
  amountBeforeDiscount?: string;
  couponCodeValue?: string;
}

interface AddressInterface {
  addressId?: number | null;
  locality: string;
  streetName: string;
  city: string;
  pin: string;
  state: string;
  country: string;
}

interface CustomerInterface {
  firstName: string;
  lastName: string;
  contactNumber: string;
  email: string;
  companyName: string;
  addresses: AddressInterface[];
}
interface LogisticsInterface {
  orderId: number;
  providerName: string | null;
  trackingId: string | null;
}

interface LogisticsModalState {
  isOpen: boolean;
  providerName: string;
  trackingId: string;
  isSubmitting: boolean;
}

interface EditLogisticsModalState {
  isOpen: boolean;
  providerName: string;
  trackingId: string;
  isSubmitting: boolean;
}

const OrderDetails = () => {
  const { orderId } = useParams<Params>();
  const [order, setOrder] = useState<orderInterface>();
  const [customer, setCustomer] = useState<CustomerInterface>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [logisticsModal, setLogisticsModal] = useState<LogisticsModalState>({
    isOpen: false,
    providerName: '',
    trackingId: '',
    isSubmitting: false,
  });
  const [editLogisticsModal, setEditLogisticsModal] = useState<EditLogisticsModalState>({
    isOpen: false,
    providerName: '',
    trackingId: '',
    isSubmitting: false,
  });
  const [logistics, setLogistics] = useState<LogisticsInterface | null>(null);

  const currentUser = AuthenticationService.getCurrentUser();
  const isSuperAdmin = currentUser?.roles?.includes('ROLE_SUPERADMIN') ?? false;

  const accessToken = getAccessToken();
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    'X-API-SECRET': `${API_SECRET}`,
    'X-API-KEY': `${API_KEY}`,
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (orderId != undefined) {
          console.log('Fetching order with ID:', orderId);
          console.log('Using headers:', headers);
          console.log('API URL:', `${BASEURL}orders/${orderId}`);
          
          const response = await axios.get(`${BASEURL}orders/${orderId}`, {
            headers: headers,
          });
          
          console.log('Order response:', response.data);
          const responseData = response.data;
          
          if (responseData.customerId) {
            console.log('Fetching customer with ID:', responseData.customerId);
            const customerResponse = await axios.get(
              `${BASEURL}customers/${responseData.customerId}`,
              {
                headers: headers,
              },
            );
            console.log('Customer response:', customerResponse.data);
            setCustomer(customerResponse.data);
          }
          
          setOrder(responseData);

          // Fetch logistics data
          try {
            const logisticsResponse = await axios.get(
              `${BASEURL}order-logistics/${orderId}`,
              { headers },
            );
            setLogistics(logisticsResponse.data);
          } catch {
            // 404 is expected when no logistics record exists
            setLogistics(null);
          }

          setIsLoading(false);
        }
      } catch (error: any) {
        setIsLoading(false);
        console.error('Error fetching order data:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        if (error.response?.status === 404) {
          setError(`Order not found with ID: ${orderId}`);
        } else if (error.response?.status === 401) {
          setError('Authentication failed. Please check your login status.');
        } else {
          setError(`Failed to fetch order details: ${error.response?.data?.message || error.message}`);
        }
      }
    };
    fetchData();
  }, [orderId]);

  const openLogisticsModal = () => {
    setLogisticsModal({
      isOpen: true,
      providerName: '',
      trackingId: '',
      isSubmitting: false,
    });
  };

  const closeLogisticsModal = () => {
    setLogisticsModal((prev) => ({ ...prev, isOpen: false }));
  };

  const openEditLogisticsModal = () => {
    setEditLogisticsModal({
      isOpen: true,
      providerName: logistics?.providerName ?? '',
      trackingId: logistics?.trackingId ?? '',
      isSubmitting: false,
    });
  };

  const closeEditLogisticsModal = () => {
    setEditLogisticsModal((prev) => ({ ...prev, isOpen: false }));
  };

  const handleEditLogisticsSubmit = async () => {
    setEditLogisticsModal((prev) => ({ ...prev, isSubmitting: true }));
    try {
      if (logistics) {
        // Record exists — update it
        await axios.put(
          `${BASEURL}order-logistics/${orderId}`,
          {
            providerName: editLogisticsModal.providerName.trim() || null,
            trackingId: editLogisticsModal.trackingId.trim() || null,
          },
          { headers },
        );
      } else {
        // No record yet — create it
        await axios.post(
          `${BASEURL}order-logistics/`,
          {
            orderId: orderId,
            providerName: editLogisticsModal.providerName.trim() || null,
            trackingId: editLogisticsModal.trackingId.trim() || null,
          },
          { headers },
        );
      }
      // Refresh logistics data
      const logisticsResponse = await axios.get(
        `${BASEURL}order-logistics/${orderId}`,
        { headers },
      );
      setLogistics(logisticsResponse.data);
      setEditLogisticsModal((prev) => ({ ...prev, isOpen: false, isSubmitting: false }));
    } catch (err) {
      console.error('Error updating logistics:', err);
      setEditLogisticsModal((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleLogisticsSubmit = async () => {
    setLogisticsModal((prev) => ({ ...prev, isSubmitting: true }));
    try {
      // Create logistics record (providerName/trackingId can be empty/null for store pickup)
      await axios.post(
        `${BASEURL}order-logistics/`,
        {
          orderId: orderId,
          providerName: logisticsModal.providerName.trim() || null,
          trackingId: logisticsModal.trackingId.trim() || null,
        },
        { headers },
      );
    } catch (err) {
      console.error('Error creating logistics record:', err);
      // Continue to mark order as completed even if logistics creation fails
    }

    try {
      await axios.get(`${BASEURL}orders/completed/${orderId}`, {
        headers,
      });
      window.location.reload();
    } catch (err) {
      console.error('Error marking order as completed:', err);
      setLogisticsModal((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const orderShipped = async () => {
    try {
      const response = await axios.get(`${BASEURL}orders/shipped/${orderId}`, {
        headers: headers,
      });
      console.log('Order shipped response:', response.data);
      window.location.reload();
    } catch (error) {
      console.error('Error marking order as shipped:', error);
    }
  };

  return (
    <>
      <Breadcrumb pageName="Order Details" />
      {isLoading ? (
        <div className="flex mt-60 items-center justify-center">
          <svg
            className="h-10 w-10 animate-spin text-primary fill-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="primary"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="primary"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A8.001 8.001 0 0117.709 7H15v5.373L6.373 17H6v-4.709zM20 12h-4v-.373l-5.373-5.372L15 6.627V2h5.373A8.001 8.001 0 0120 12z"
            ></path>
          </svg>
        </div>
      ) : order ? (
        <div className="">
          <div className="hidden xl:flex mb-2 justify-between">
            <div className="">
              <div className="flex space-x-8">
                <h2 className="text-lg md:text-sm   xl:text-base font-medium text-heading  ">
                  Order ID: {order.orderId}
                </h2>
                <h2 className="text-lg  md:text-sm xl:text-base font-medium text-heading ">
                  Date : {order.date.toString().split('T')[0]}
                </h2>
                <h2
  className={`text-lg md:text-sm xl:text-base font-medium text-heading ${
    order.status === 'PAYMENT_DONE' ? 'text-success' : ''
  }`}
>                  Status :{' '}
                  {order.status === 'PAYMENT_DONE' &&
                  order.paymentMode === 'cod'
                    ? 'PARTIAL_PAYMENT'
                    : order.status}
                </h2>
                
                 {order.paymentMode && (               
                  <h2 className="text-lg md:text-sm  xl:text-base font-medium text-heading">
                    Payment Type : {order.paymentMode}
                  </h2>               
              )}

{order.status === 'PAYMENT_DONE' &&
                    order.paymentMode === 'cod' && (
                      <h2 className="text-lg md:text-sm xl:text-base font-medium text-heading">
                        Paid Amount : 99 
                      </h2>
                    )}
              </div>

              <div className="flex space-x-8 mt-2">
                <h2 className="text-lg md:text-sm   xl:text-base font-medium text-heading">
                  Customer: {customer?.firstName} {customer?.lastName}
                </h2>

                <h2 className="text-lg md:text-sm  xl:text-base font-medium text-heading">
                  Contact No.: {customer?.contactNumber}
                </h2>

                <h2 className="text-lg md:text-sm xl:text-base font-medium text-heading">
                  Email: {customer?.email}
                </h2>

                {order.appliedCouponCode && (
                  <h2 className="text-lg md:text-sm xl:text-base font-medium text-success">
                    Coupon: {order.appliedCouponCode} (-₹{order.discountAmount})
                  </h2>
                )}
              </div>

             
            </div>

            {(order.status === 'ORDER_PLACED' ||
            order.status === 'PAYMENT_DONE') && (
              <div>
                <button
                  onClick={openLogisticsModal}
                  className=" ml-16 inline-block rounded bg-success px-4 py-1 text-sm font-medium text-gray hover:bg-opacity-90"
                >
                  <p>Mark as</p>
                  <p>Order Completed</p>
                </button>
              </div>
            )}
          </div>

          <div className="hidden xl:flex">
            <table className=" w-full bg-white mt-3">
              <thead>
                <tr className="bg-gray-500 text-left dark:bg-meta-4 border-b border-[#eee] ">
                  <th className="px-4 py-5 pl-5 font-semibold text-black dark:text-white xl:pl-11">
                    Product
                  </th>

                  <th className=" px-4 py-5 pl-9 font-semibold text-black dark:text-white">
                    Option
                  </th>

                  <th className=" px-4 py-5 pl-9 font-semibold text-black dark:text-white">
                    Price
                  </th>

                  <th className="px-4 py-5  font-semibold text-black dark:text-white">
                    Quantity
                  </th>

                  <th className="px-4 py-5  font-semibold text-black dark:text-white">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {order &&
                  order.orderDetails.map((order: any, key: any) => (
                    <tr className="border-b border-[#eee] text-black">
                      <td className="border-b border-[#eee]  py-5 pl-9 dark:border-strokedark ">
                        <div className="sm:flex items-center gap-5 ">
                          <img
                            src={
                              order.productImageUrl ??
                              '/assets/placeholder/order-product.svg'
                            }
                            width="64"
                            height="64"
                            className="object-cover"
                          />{' '}
                          <div>{order.productName}</div>
                        </div>
                      </td>

                      <td className="border-b border-[#eee] px-4 py-5 pl-9 dark:border-strokedark ">
                        {order.option ? order.option : 'Not Available'}
                      </td>

                      <td className="border-b border-[#eee] px-4 py-5 pl-9 dark:border-strokedark ">
                        {order.price}
                      </td>

                      <td className="border-b border-[#eee] px-4 py-5  dark:border-strokedark ">
                        {order.quantity}
                      </td>

                      <td className="border-b border-[#eee] px-4 py-5  dark:border-strokedark ">
                        ₹. {order.amount}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="xl:hidden sm:block ">
            <div className="flex justify-between mb-4">
              <div className="space-y-0.5 text-base">
                <h2 className="text-base md:text-sm mr-2  xl:text-lg font-medium text-heading  ">
                  Order ID: {order.orderId}
                </h2>

                <h2 className="text-base md:text-sm mr-2  xl:text-lg font-medium text-heading  ">
                  Date: {order.date.toString().split('T')[0]}
                </h2>

                <h2 className={`md:text-sm mr-2 xl:mr-10 xl:text-base font-medium text-heading ${
    order.status === 'PAYMENT_DONE' ? 'text-success' : ''
  }`}>
                  Status :{' '}
                  {order.status === 'PAYMENT_DONE' &&
                  order.paymentMode === 'cod'
                    ? 'PARTIAL_PAYMENT'
                    : order.status}
                </h2>

                {order.paymentMode && (
                  <h2 className=" md:text-sm mr-2 xl:mr-10 xl:text-base font-medium text-heading">
                    Payment Type :{' '}
                    {order.paymentMode === 'full' ? 'Full Payment' : 'COD'}
                  </h2>
                )}

                {order.status === 'PAYMENT_DONE' &&
                  order.paymentMode === 'cod' && (
                    <h2 className=" md:text-sm mr-2 xl:mr-10 xl:text-base font-medium text-heading">
                      Paid Amount : 99 (COD charge)
                    </h2>
                  )}

                <h2 className=" md:text-sm mr-2 xl:mr-10 xl:text-base font-medium text-heading">
                  Customer: {customer?.firstName} {customer?.lastName}
                </h2>

                <h2 className=" md:text-sm mr-2 xl:mr-10 xl:text-base font-medium text-heading">
                  Contact No.: {customer?.contactNumber}
                </h2>
                <h2 className=" md:text-sm mr-2 xl:mr-10 xl:text-base font-medium text-heading">
                  Email : {customer?.email}
                </h2>

                {order.appliedCouponCode && (
                  <h2 className=" md:text-sm mr-2 xl:mr-10 xl:text-base font-medium text-success">
                    Coupon Applied: {order.appliedCouponCode} (-₹{order.discountAmount})
                  </h2>
                )}
              </div>
              <div className="">
                {order.status === 'ORDER_PLACED' ||
                order.status === 'PAYMENT_DONE' ? (
                  <div>
                    <button
                      onClick={openLogisticsModal}
                      className="  inline-block rounded bg-success px-3 py-2  text-xs font-medium text-gray hover:bg-opacity-90"
                    >
                      <p>Mark as</p> <p> Order Completed</p>
                    </button>
                  </div>
                ) : (
                  order.status === 'PAYMENT_DONE' && (
                    <div>
                      <button
                        onClick={orderShipped}
                        className=" inline-block rounded bg-success px-3 py-2 text-xs font-medium text-gray hover:bg-opacity-90"
                      >
                        <p>Mark as</p>
                        <p>Order Shipped</p>
                      </button>
                    </div>
                  )
                )}
              </div>
            </div>
            {order &&
              order.orderDetails.map((order: any, key: any) => (
                <div className=" bg-white p-2 border-b border-[#eee] mt-1">
                  <div className="flex items-center gap-5 mb-1 text-sm my-1 text-black">
                    <img
                      src={
                        order.productImageUrl ??
                        '/assets/placeholder/order-product.svg'
                      }
                      width="64"
                      height="64"
                      className="object-cover"
                    />
                    <div className="font-medium space-y-0.5">
                      <p className="font-bold text-xs">{order.productName}</p>
                      <p>₹. {order.price}</p>
                      <div className="flex space-x-5">
                        <p>Quantity : {order.quantity}</p>
                        <p>Total : {order.amount}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
          <div className="flex  w-full justify-end pr-2 mt-1 xl:pr-8 text-black font-semibold bg-white right-0 py-3 xl:py-5 text-normal">
            <div>
              <p> Sub Total: ₹. {order.amountBeforeDiscount || order.amount}</p>
              {order.appliedCouponCode && (
                <>
                  <p className="text-success"> 
                    Coupon Applied ({order.appliedCouponCode}): -₹. {order.discountAmount}
                  </p>
                  <p className="text-sm text-gray-600">
                    Discount: {order.appliedCouponCode}
                  </p>
                </>
              )}
              <p>
                {' '}
                Shipping Cost: ₹.{' '}
                {order.flatShipping ? order.flatShipping : '0.00'}
              </p>
              <p> Order Total : ₹. {order.orderTotal}</p>
            </div>
          </div>

          {order.appliedCouponCode && (
            <div className=" bg-white p-3  xl:px-6 mt-3 border-l-4 border-success">
              <h2 className="text-lg md:text-xl xl:text-xl font-semibold text-black py-1 mb-2">
                Discount Applied:
              </h2>
              <div className=" text-black">
                <div className="flex items-center gap-4">
                  <div className="bg-success/10 px-3 py-1 rounded-md">
                    <span className="text-success font-semibold">
                      {order.appliedCouponCode}
                    </span>
                  </div>
                  <div className="text-success font-semibold">
                    Discount: ₹{order.discountAmount}
                  </div>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  Original Amount: ₹{order.amountBeforeDiscount || order.amount}
                </div>
                <div className="text-sm text-gray-600">
                  Final Amount: ₹{order.orderTotal}
                </div>
              </div>
            </div>
          )}

          {order.status != 'ORDER_PENDING' && (
            <div className=" bg-white p-3  xl:px-6 mt-3 ">
              <h2 className="text-lg md:text-xl xl:text-xl font-semibold text-black py-1 mb-2">
                Payment gateway details:
              </h2>
              <div className=" text-black">
                <div className=""> Payment gateway : HDFC</div>
                <div className="">
                  Payment Id : {order.paymentId ? order.paymentId : 'N/A'}
                </div>
                <div className="">Order Id : {order.paymentOrderId}</div>
                <div className="">Status : {order.paymentStatus}</div>
              </div>
            </div>
          )}

          {/* Logistics / Shipping Info */}
          {logistics ? (
            <div className="bg-white p-3 xl:px-6 mt-3">
              <div className="flex items-center justify-between py-1 mb-2">
                <h2 className="text-lg md:text-xl xl:text-xl font-semibold text-black">
                  Logistics / Shipping:
                </h2>
                {isSuperAdmin && order.status === 'ORDER_COMPLETED' && (
                  <button
                    onClick={openEditLogisticsModal}
                    className="inline-flex items-center gap-1.5 rounded border border-primary px-3 py-1 text-xs font-medium text-primary hover:bg-primary hover:text-white transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                    Edit Logistics
                  </button>
                )}
              </div>
              <div className="text-black space-y-1">
                <div>
                  Provider:{' '}
                  <span className="font-medium">
                    {logistics.providerName && logistics.providerName.trim()
                      ? logistics.providerName
                      : <span className="text-gray-400 italic">N/A</span>}
                  </span>
                </div>
                <div>
                  Tracking ID:{' '}
                  <span className="font-medium">
                    {logistics.trackingId && logistics.trackingId.trim()
                      ? logistics.trackingId
                      : <span className="text-gray-400 italic">N/A</span>}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            order.status === 'ORDER_COMPLETED' && (
              <div className="bg-white p-3 xl:px-6 mt-3">
                <div className="flex items-center justify-between py-1 mb-2">
                  <h2 className="text-lg md:text-xl xl:text-xl font-semibold text-black">
                    Logistics / Shipping:
                  </h2>
                  {isSuperAdmin && (
                    <button
                      onClick={openEditLogisticsModal}
                      className="inline-flex items-center gap-1.5 rounded border border-primary px-3 py-1 text-xs font-medium text-primary hover:bg-primary hover:text-white transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                      Add Logistics
                    </button>
                  )}
                </div>
                <div className="text-gray-400 italic text-sm">No logistics record (Store Pickup).</div>
              </div>
            )
          )}

          {order.shippingAddress == 'storePickup' ? (
            <div className=" bg-white p-3  xl:px-6 mt-3 ">
              <h2 className="text-lg md:text-xl xl:text-xl font-semibold text-black py-1 mb-2">
                Delivery Address:
              </h2>
              <div className=" text-black">
                <p>
                  {customer?.firstName} {customer?.lastName}
                </p>
                <p>Order pick Up from Motoioi shop</p>
                <p>{customer?.email}</p>

                <p>{customer?.contactNumber}</p>
              </div>
            </div>
          ) : (
            <div className=" bg-white p-3  xl:px-6 mt-3 ">
              <h2 className="text-lg md:text-xl xl:text-xl font-semibold text-black py-1 mb-2">
                Delivery Address:
              </h2>
              <div className=" text-black ">
                <p>
                  {customer?.firstName} {customer?.lastName}
                </p>

                <p>{order?.shippingAddress}.</p>

                <p>{customer?.email}</p>

                <p>{customer?.contactNumber}</p>
              </div>
            </div>
          )}

          <div className=" bg-white p-3  xl:px-6 mt-3 mb-10">
            <h2 className="text-lg md:text-xl xl:text-xl font-semibold text-black py-1 mb-2">
              Customer Address:
            </h2>
            <div className=" text-black ">
              <p>
                {customer?.addresses[0].locality},{' '}
                {customer?.addresses[0].streetName},{' '}
                {customer?.addresses[0].city}, {customer?.addresses[0].pin},{' '}
                {customer?.addresses[0].state}, {customer?.addresses[0].country}
                .
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center mt-20">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-500 mb-4">
              {error || `Order not found with Order ID: ${orderId}`}
            </h2>
            <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg mb-4">
              <h3 className="font-semibold mb-2">Debug Information:</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Order ID: {orderId}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">API URL: {BASEURL}orders/{orderId}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Authentication: {accessToken ? 'Token present' : 'No token found'}
              </p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 mr-4"
            >
              Retry
            </button>
            <button
              onClick={() => window.history.back()}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Go Back
            </button>
          </div>
        </div>
      )}

      {/* Edit Logistics Modal (SUPERADMIN only) */}
      {editLogisticsModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeEditLogisticsModal}
          />
          <div className="relative z-10 bg-white dark:bg-boxdark rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-semibold text-black dark:text-white mb-1">
              Edit Logistics / Shipping
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              Update provider name and tracking ID for this order.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Provider Name
              </label>
              <input
                type="text"
                value={editLogisticsModal.providerName}
                onChange={(e) =>
                  setEditLogisticsModal((prev) => ({
                    ...prev,
                    providerName: e.target.value,
                  }))
                }
                placeholder="e.g. FedEx, DHL, Store Pickup (optional)"
                className="w-full rounded border border-stroke bg-transparent px-4 py-2 text-black dark:text-white dark:border-strokedark focus:border-primary focus:outline-none"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Tracking ID
              </label>
              <input
                type="text"
                value={editLogisticsModal.trackingId}
                onChange={(e) =>
                  setEditLogisticsModal((prev) => ({
                    ...prev,
                    trackingId: e.target.value,
                  }))
                }
                placeholder="e.g. 1Z999AA10123456784 (optional)"
                className="w-full rounded border border-stroke bg-transparent px-4 py-2 text-black dark:text-white dark:border-strokedark focus:border-primary focus:outline-none"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={closeEditLogisticsModal}
                disabled={editLogisticsModal.isSubmitting}
                className="rounded border border-stroke px-5 py-2 text-sm font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-meta-4 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditLogisticsSubmit}
                disabled={editLogisticsModal.isSubmitting}
                className="rounded bg-primary px-5 py-2 text-sm font-medium text-white hover:bg-opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {editLogisticsModal.isSubmitting && (
                  <svg
                    className="h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A8.001 8.001 0 0117.709 7H15v5.373L6.373 17H6v-4.709zM20 12h-4v-.373l-5.373-5.372L15 6.627V2h5.373A8.001 8.001 0 0120 12z" />
                  </svg>
                )}
                {editLogisticsModal.isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logistics Modal */}
      {logisticsModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeLogisticsModal}
          />
          {/* Modal */}
          <div className="relative z-10 bg-white dark:bg-boxdark rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-semibold text-black dark:text-white mb-1">
              Mark Order as Completed
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              Optionally enter logistics details. Leave blank for store pickup.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Provider Name
              </label>
              <input
                type="text"
                value={logisticsModal.providerName}
                onChange={(e) =>
                  setLogisticsModal((prev) => ({
                    ...prev,
                    providerName: e.target.value,
                  }))
                }
                placeholder="e.g. FedEx, DHL (optional)"
                className="w-full rounded border border-stroke bg-transparent px-4 py-2 text-black dark:text-white dark:border-strokedark focus:border-primary focus:outline-none"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Tracking ID
              </label>
              <input
                type="text"
                value={logisticsModal.trackingId}
                onChange={(e) =>
                  setLogisticsModal((prev) => ({
                    ...prev,
                    trackingId: e.target.value,
                  }))
                }
                placeholder="e.g. 1Z999AA10123456784 (optional)"
                className="w-full rounded border border-stroke bg-transparent px-4 py-2 text-black dark:text-white dark:border-strokedark focus:border-primary focus:outline-none"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={closeLogisticsModal}
                disabled={logisticsModal.isSubmitting}
                className="rounded border border-stroke px-5 py-2 text-sm font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-meta-4 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleLogisticsSubmit}
                disabled={logisticsModal.isSubmitting}
                className="rounded bg-success px-5 py-2 text-sm font-medium text-white hover:bg-opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {logisticsModal.isSubmitting && (
                  <svg
                    className="h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A8.001 8.001 0 0117.709 7H15v5.373L6.373 17H6v-4.709zM20 12h-4v-.373l-5.373-5.372L15 6.627V2h5.373A8.001 8.001 0 0120 12z"
                    />
                  </svg>
                )}
                {logisticsModal.isSubmitting ? 'Processing...' : 'Confirm & Complete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default OrderDetails;
